import { FinancialIndependenceValues, FinancialIndependenceResult } from './types';

export function calculateFinancialIndependence(
  values: FinancialIndependenceValues
): FinancialIndependenceResult {
  const { monthlyExpenses, dividendYield, currentSavings, monthlyContribution, investmentReturn } = values;
  
  // Target amount needed for financial independence
  const targetAmount = (monthlyExpenses * 12) / (dividendYield / 100);
  
  // Calculate years to reach financial independence using investment return for growth
  const annualReturn = investmentReturn / 100;
  const monthlyReturn = annualReturn / 12;
  
  let years = 0;
  let currentAmount = currentSavings;
  
  // If there is no capital and no contributions, goal is not achievable
  if (currentAmount <= 0 && monthlyContribution <= 0) {
    return {
      targetAmount,
      yearsToIndependence: -1, // Special value to indicate not achievable
      monthlyDividendIncome: 0,
      totalSavings: 0,
      totalReturn: 0,
      finalAmount: 0,
    };
  }

  // If we already have enough, return 0 years
  if (currentAmount >= targetAmount) {
    return {
      targetAmount,
      yearsToIndependence: 0,
      monthlyDividendIncome: currentAmount * (dividendYield / 100) / 12,
      totalSavings: currentAmount,
      totalReturn: 0,
      finalAmount: currentAmount
    };
  }
  
  // Calculate years needed
  while (currentAmount < targetAmount && years < 100) { // Cap at 100 years
    years++;
    currentAmount = currentAmount * (1 + annualReturn) + (monthlyContribution * 12);
  }
  
  // Calculate final values
  const totalSavings = currentSavings + (monthlyContribution * 12 * years);
  const totalReturn = currentAmount - totalSavings;
  const monthlyDividendIncome = targetAmount * (dividendYield / 100) / 12;
  
  return {
    targetAmount,
    yearsToIndependence: years,
    monthlyDividendIncome,
    totalSavings,
    totalReturn,
    finalAmount: currentAmount
  };
}

